import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, Clock, Sparkles, HandHeart } from "lucide-react";
import { http, money, trackEvent, errMsg } from "../lib/market";
import { Eyebrow } from "../components/Section";
import { toast } from "sonner";

const PRICING_LABEL = {
  fixed: "Fixed fee", quotation: "Lawyer quotation", hourly: "Time-based", milestone: "Milestones",
  package: "Package", sponsored: "Sponsored / legal aid",
};

export const ServiceCard = ({ s, i }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}>
    <Link to={`/services/${s.service_id}`} data-testid={`service-card-${s.service_id}`}
      className="group block h-full glass rounded-2xl p-6 border border-white/8 hover:border-turquoise/40 transition-colors">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="aj-chip !py-1 !text-[9px]">{PRICING_LABEL[s.pricing_model]}</span>
        {s.wallet_eligibility && <span className="aj-chip aj-chip-gold !py-1 !text-[9px]"><HandHeart className="w-3 h-3" /> Wallet eligible</span>}
      </div>
      <h3 className="font-playfair text-xl text-white mb-2 group-hover:text-turquoise transition-colors">{s.service_name}</h3>
      <p className="font-inter text-sm text-white/55 leading-relaxed mb-5 line-clamp-3">{s.description}</p>
      <div className="flex items-center justify-between font-inter text-xs text-white/50 mt-auto">
        <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {s.estimated_turnaround}</span>
        <span className="text-gold-200 font-montserrat tracking-wide">{s.fixed_fee_minor ? money(s.fixed_fee_minor) : "Quoted"}</span>
      </div>
    </Link>
  </motion.div>
);

export default function ServiceCatalogue() {
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [services, setServices] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const cat = params.get("category") || "";

  useEffect(() => {
    http.get("/services/categories").then(({ data }) => setCats(data.categories)).catch((e) => toast.error(errMsg(e)));
    trackEvent("service_catalogue_viewed");
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      http.get("/services", { params: { category_id: cat || undefined, q: q || undefined, jurisdiction_id: "NG" } })
        .then(({ data }) => setServices(data.services)).catch((e) => toast.error(errMsg(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [cat, q]);

  const pick = (id) => setParams(id ? { category: id } : {});

  return (
    <main className="bg-ink text-white min-h-screen" data-testid="service-catalogue-page">
      <section className="relative overflow-hidden border-b border-white/8">
        <div className="turquoise-aura -top-40 -left-40 opacity-40" />
        <div className="container mx-auto px-5 sm:px-8 max-w-7xl py-16 relative z-10">
          <Eyebrow>Legal Services Marketplace · Nigeria (pilot)</Eyebrow>
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] max-w-3xl">
            Professional legal help, <span className="italic text-aj-gradient">clearly priced.</span>
          </h1>
          <p className="font-inter text-base text-white/60 mt-5 max-w-2xl">
            Browse verified services, or describe your problem in plain words and Justice-AI will suggest the right one.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-3xl">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
              <input data-testid="catalogue-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search e.g. tenancy, trademark, will…"
                className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-3.5 font-inter text-sm focus:outline-none focus:border-turquoise/60" />
            </div>
            <Link to="/request-service" data-testid="catalogue-describe-btn" className="btn-aj-primary whitespace-nowrap">
              <Sparkles className="w-4 h-4" /> Describe my problem
            </Link>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-5 sm:px-8 max-w-7xl py-12 grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="space-y-1" data-testid="catalogue-categories">
          <button onClick={() => pick("")} data-testid="category-all"
            className={`w-full text-left px-4 py-2.5 rounded-xl font-montserrat text-[11px] uppercase tracking-wider transition-colors ${!cat ? "bg-turquoise text-ink font-semibold" : "text-white/65 hover:bg-white/5"}`}>
            All services
          </button>
          {cats.map((c) => (
            <button key={c.category_id} onClick={() => pick(c.category_id)} data-testid={`category-${c.category_id}`}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-inter text-sm flex justify-between transition-colors ${cat === c.category_id ? "bg-turquoise/15 text-turquoise border border-turquoise/30" : "text-white/65 hover:bg-white/5 border border-transparent"}`}>
              <span>{c.name}</span><span className="text-white/30 text-xs">{c.service_count}</span>
            </button>
          ))}
        </aside>
        <section>
          <div className="flex items-center justify-between mb-6">
            <p className="font-inter text-sm text-white/50" data-testid="catalogue-count">{services.length} services available</p>
            <Link to="/client/hub" className="btn-aj-ghost text-xs" data-testid="catalogue-hub-link">My matters <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {services.map((s, i) => <ServiceCard key={s.service_id} s={s} i={i} />)}
          </div>
        </section>
      </div>
    </main>
  );
}
